/**
 * Packaging OS — useLocaleSwitch (Phase 1R-A-Fix-5 → Phase 1R-C).
 *
 * Returns the function the LanguageSwitcher should call. Three rules:
 *
 *   1. Update the module-level locale state via `useLocale().setLocale`
 *      so the cookie + `<html lang dir>` + every subscriber catches up.
 *   2. If the current URL is already locale-prefixed (`/<en|ar|fa|tr>/...`),
 *      rewrite the prefix to the new locale.
 *   3. If the current URL is a legacy non-prefixed route, prepend the
 *      new locale so the URL becomes the canonical, shareable form for
 *      the chosen language. (Phase 1R-C — fixes F-UX-001 "language
 *      state and URL prefix can diverge on app routes".)
 *
 * The root `/` becomes `/<locale>` (the `:locale` index route renders
 * the public landing). Other paths become `/<locale><pathname>`.
 *
 * Search/hash are preserved so `/fa/login?postauth=1` etc. keep their
 * query string.
 */

import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { findLocale, type LocaleCode } from "./locales";
import { useLocale } from "./useLocale";

const LOCALE_PREFIX_RE = /^\/(en|ar|fa|tr)(\/.*)?$/;

export function useLocaleSwitch(): (next: LocaleCode) => void {
  const [, setLocale] = useLocale();
  const navigate = useNavigate();
  const location = useLocation();

  return useCallback(
    (next: LocaleCode) => {
      if (!findLocale(next)) return;
      setLocale(next);

      const path = location.pathname;
      const match = path.match(LOCALE_PREFIX_RE);

      let target: string;
      if (match) {
        // Already prefixed → swap the prefix.
        const rest = match[2] ?? "";
        target = `/${next}${rest}`;
      } else {
        // Legacy non-prefixed → prepend the locale.
        // `/`           → `/<next>`
        // `/app`        → `/<next>/app`
        // `/app/cat/x`  → `/<next>/app/cat/x`
        const trimmed = path === "/" ? "" : path;
        target = `/${next}${trimmed}`;
      }
      target = `${target}${location.search}${location.hash}`;
      // Replace so the language flip does not stack on history.
      navigate(target, { replace: true });
    },
    [setLocale, navigate, location.pathname, location.search, location.hash],
  );
}
